import { diaryDay, type DiaryDayRow } from './diaryDuplicates';

/**
 * The "Letzte vergebene Nrn." of a Tagebuch day and the check over them: a
 * number handed out so far can only grow, so a day naming a SMALLER one than
 * the day before it is almost always a typo. The check is advisory — a
 * renumbering or a correction on a later day is legitimate — so the form says
 * so and saves anyway.
 */

/**
 * The last-number fields the form lists as fields of their own. The Sondage is
 * one of them, apart from the day's Sondage/Teilfoto list: the last Sondage
 * HANDED OUT, not the ones worked that day.
 */
export const DIARY_FORM_NUMBER_FIELDS = [
    'lastFindingNumber',
    'lastProfileNumber',
    'lastStrayFindNumber',
    'lastGeoprofileNumber',
    'lastSondageNumber'
] as const;

export type DiaryFormNumberField = (typeof DIARY_FORM_NUMBER_FIELDS)[number];

/**
 * All six on the doc. The Teilfoto is not typed in as a single field — it is
 * the highest of the day's Sondage/Teilfoto list (see `lastPartialPhotoNumber`)
 * — but it is still recorded.
 */
export const DIARY_NUMBER_FIELDS = [
    ...DIARY_FORM_NUMBER_FIELDS,
    'lastPartialPhotoNumber'
] as const;

export type DiaryNumberField = (typeof DIARY_NUMBER_FIELDS)[number];

/** A day's last numbers, as the form holds them (all free text). */
export type DiaryNumbers = Record<DiaryNumberField, string>;

/**
 * The previous day's numbers, trimmed — what the day being written may carry
 * over. All empty when there is no previous day, so a caller can read a field
 * without asking whether one exists.
 */
export function carryOverNumbers(previous: DiaryNumbers | null): DiaryNumbers {
    return Object.fromEntries(
        DIARY_NUMBER_FIELDS.map((field) => [
            field,
            (previous?.[field] ?? '').trim()
        ])
    ) as DiaryNumbers;
}

/** One field that went backwards, with both readings for the message. */
export interface DiaryNumberWarning {
    field: DiaryNumberField;
    value: string;
    previous: string;
}

/**
 * The leading whole number of a hand-typed record number ("12a" -> 12), or null
 * when the text starts with no number at all ("-", "keine"). Only the leading
 * digits count: a suffix distinguishes records that share a number, it does not
 * order them.
 */
export function leadingNumber(value: string): number | null {
    const match = /^\s*(\d+)/.exec(value ?? '');
    return match ? Number(match[1]) : null;
}

/**
 * The project's Tagebuch day BEFORE `recordedAt` — the one whose numbers the
 * edited day has to keep up with. `excludeId` skips the row being edited; days
 * without a usable date are ignored.
 */
export function findPreviousDiaryDay<T extends DiaryDayRow>(
    diaries: readonly T[],
    projectId: string,
    recordedAt: string,
    excludeId: string
): T | null {
    const day = diaryDay(recordedAt);
    if (day === '') {
        return null;
    }
    let previous: T | null = null;
    for (const diary of diaries) {
        if (diary.id === excludeId || diary.projectId !== projectId) {
            continue;
        }
        const other = diaryDay(diary.recordedAt);
        if (other === '' || other >= day) {
            continue;
        }
        if (!previous || other > diaryDay(previous.recordedAt)) {
            previous = diary;
        }
    }
    return previous;
}

/**
 * The TYPED fields whose number is smaller than the previous day's. Equal
 * numbers pass — a day on which nothing new was dug hands out no new number —
 * and a field either side leaves blank is skipped.
 *
 * The typed Sondage is among them — it is the last one handed out, not one
 * worked that day. The Teilfoto is NOT: the highest Teilfoto of two days says
 * nothing about either. What cannot happen is the same Sondage carrying a lower
 * Teilfoto later, and that check is per Sondage (see
 * sondagePhotosWentBackwards).
 */
export function diaryNumbersWentBackwards(
    values: Record<DiaryFormNumberField, string>,
    previous: DiaryNumbers | null
): DiaryNumberWarning[] {
    if (!previous) {
        return [];
    }
    const warnings: DiaryNumberWarning[] = [];
    for (const field of DIARY_FORM_NUMBER_FIELDS) {
        const value = values[field] ?? '';
        const before = previous[field] ?? '';
        const current = leadingNumber(value);
        const earlier = leadingNumber(before);
        if (current === null || earlier === null || current >= earlier) {
            continue;
        }
        warnings.push({ field, value: value.trim(), previous: before.trim() });
    }
    return warnings;
}
